'use client';

import { useTranslations } from 'next-intl';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { deriveBatchConfidence } from '../mappers/qr-review.mapper';
import type { QrReviewBatch, QrReviewPageRecord } from '../types';

// ─── Props ───────────────────────────────────────────────────────────────────

interface QrReviewVerdictPanelProps {
  batch: QrReviewBatch;
  selectedPage: QrReviewPageRecord | null;
  pages: QrReviewPageRecord[];
  // QR scan state
  qrScanning: boolean;
  qrScanResult: 'match' | 'mismatch' | null;
  scanMismatch: { expected: string; scanned: string } | null;
  scannedQrValue: string | null;
  onRescanQr: () => void;
  // Verdict actions
  onManualVerdict: (verdict: 'passed' | 'rejected') => void;
  isVerdictPending: boolean;
  // Session
  canEndSession: boolean;
  isEndingSession: boolean;
  onEndSession: () => void;
  // Batch reject (rewinds container to handover or photo-scanning)
  onRejectBatch?: () => void;
  canRejectBatch?: boolean;
  // Read-only embed (hides all action controls)
  readOnly?: boolean;
  /** Overrides the fixed `w-80` — passed `w-full` when embedded in a Sheet. */
  className?: string;
}

/** Below this the bar turns red — a signal for the reviewer, not a gate. */
const CONFIDENCE_THRESHOLD = 90;

// ─── QrReviewVerdictPanel ────────────────────────────────────────────────────

export function QrReviewVerdictPanel({
  batch,
  selectedPage,
  pages,
  qrScanning,
  qrScanResult,
  scanMismatch,
  scannedQrValue,
  onRescanQr,
  onManualVerdict,
  isVerdictPending,
  canEndSession,
  isEndingSession,
  onEndSession,
  onRejectBatch,
  canRejectBatch = false,
  readOnly = false,
  className
}: QrReviewVerdictPanelProps) {
  const t = useTranslations('qrReview');

  // Fall back to the persisted page record when the live decoder hasn't
  // produced a fresh result (e.g. blocked by a fetch-intercepting browser
  // extension, or the user just landed on the page). The persisted values
  // were saved during photo-scanning upload and are authoritative until a
  // rescan replaces them.
  const persistedMatch = selectedPage?.qr_status === 'success';
  const persistedMismatch =
    selectedPage?.qr_status === 'mismatched' ||
    selectedPage?.qr_status === 'failed';
  const effectiveScannedQr = scannedQrValue ?? selectedPage?.barcode ?? null;
  const effectiveMatch = qrScanResult === 'match' || persistedMatch;
  const effectiveMismatch =
    qrScanResult === 'mismatch' || (!effectiveMatch && persistedMismatch);
  const effectiveScanMismatch = scanMismatch
    ? scanMismatch
    : persistedMismatch && selectedPage?.barcode
      ? { expected: '', scanned: selectedPage.barcode }
      : null;

  return (
    <aside
      className={cn(
        'flex w-80 flex-shrink-0 flex-col gap-6 overflow-y-auto border-s p-4',
        className
      )}
    >
      {/* DOCUMENT IDENTITY */}
      <div>
        <p className='text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase'>
          {t('sectionDocumentIdentity')}
        </p>
        <p className='text-muted-foreground mb-1 text-xs tracking-wide uppercase'>
          {t('serialId')}
        </p>
        <p className='mb-4 text-2xl font-bold'>
          {selectedPage
            ? `${batch.container_id}-P${selectedPage.sequence}`
            : (batch.container_id ?? '—')}
        </p>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <p className='text-muted-foreground mb-1 text-xs tracking-wide uppercase'>
              {t('pagePosition')}
            </p>
            <p className='text-sm font-semibold'>
              {t('pageOf', {
                current: selectedPage?.sequence ?? 0,
                total: pages.length
              })}
            </p>
          </div>
          <div>
            <p className='text-muted-foreground mb-1 text-xs tracking-wide uppercase'>
              {t('scannerId')}
            </p>
            <p className='text-sm font-semibold'>{batch.scanner_id ?? '—'}</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* PAGE VERDICT — QR scan only */}
      <div>
        <p className='text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase'>
          {t('sectionPageVerdict')}
        </p>

        {selectedPage?.qa_verdict !== null ? (
          /* Already reviewed */
          <div
            className={cn(
              'flex items-center gap-2 rounded-xl border p-3',
              selectedPage?.qa_verdict === 'passed'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-red-500 bg-red-50 text-red-700'
            )}
          >
            {selectedPage?.qa_verdict === 'passed' ? (
              <CheckCircle2 className='h-5 w-5 shrink-0' />
            ) : (
              <XCircle className='h-5 w-5 shrink-0' />
            )}
            <span className='text-sm font-semibold'>
              {selectedPage?.qa_verdict === 'passed'
                ? t('statusQaPassed')
                : t('statusQaFailed')}
            </span>
          </div>
        ) : (
          <>
            {/* Auto QR scan status */}
            {qrScanning ? (
              <div className='mb-3 flex items-center justify-center gap-2 py-2 text-sm text-blue-600'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span>{t('validating')}</span>
              </div>
            ) : effectiveMatch ? (
              <div className='mb-3 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-green-700'>
                <CheckCircle2 className='h-4 w-4 shrink-0' />
                <span className='text-xs font-semibold'>{t('qrVerified')}</span>
              </div>
            ) : effectiveScanMismatch || effectiveMismatch ? (
              <div className='mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700'>
                <div className='flex items-center gap-1.5'>
                  <AlertTriangle className='h-4 w-4 shrink-0' />
                  <span className='text-xs font-semibold'>
                    {t('qrMismatch')}
                  </span>
                </div>
              </div>
            ) : (
              <div className='mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-700'>
                <div className='flex items-center gap-1.5'>
                  <AlertTriangle className='h-4 w-4 shrink-0' />
                  <span className='text-xs font-semibold'>
                    {t('qrNotDetected')}
                  </span>
                </div>
              </div>
            )}

            {/* Manual verdict buttons */}
            {!readOnly && (
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  className='flex-1 border-green-300 text-green-700 hover:bg-green-50'
                  onClick={() => onManualVerdict('passed')}
                  disabled={isVerdictPending}
                >
                  {isVerdictPending ? (
                    <Loader2 className='me-2 h-4 w-4 animate-spin' />
                  ) : (
                    <CheckCircle2 className='me-2 h-4 w-4' />
                  )}
                  {t('verdictPass')}
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='flex-1 border-red-300 text-red-700 hover:bg-red-50'
                  onClick={() => onManualVerdict('rejected')}
                  disabled={isVerdictPending}
                >
                  <XCircle className='me-2 h-4 w-4' />
                  {t('verdictFail')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Separator />

      {/* QUALITY METRICS */}
      <div>
        <p className='text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase'>
          {t('sectionQualityMetrics')}
        </p>

        {/* QR validation row */}
        <div className='mb-4 flex items-center justify-between'>
          <div className='flex items-center gap-1'>
            <span className='text-sm'>{t('scannedQR')}</span>
            {!readOnly && (
              <button
                type='button'
                className='text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors'
                onClick={onRescanQr}
                disabled={qrScanning}
                title={t('rescanQR')}
              >
                <RefreshCw
                  className={cn('h-3 w-3', qrScanning && 'animate-spin')}
                />
              </button>
            )}
          </div>
          <div className='flex items-center gap-1.5'>
            {qrScanning ? (
              <Loader2 className='h-3 w-3 animate-spin text-blue-500' />
            ) : (
              <>
                <span className='max-w-[120px] truncate font-mono text-xs font-semibold'>
                  {effectiveScannedQr ?? '—'}
                </span>
                <div
                  className={cn(
                    'h-2 w-2 rounded-full',
                    effectiveMatch
                      ? 'bg-green-500'
                      : effectiveMismatch
                        ? 'bg-red-500'
                        : 'bg-gray-300'
                  )}
                />
              </>
            )}
          </div>
        </div>

        {/* Resolution row */}
        <div className='mb-4 flex items-center justify-between'>
          <span className='text-sm'>{t('labelResolution')}</span>
          <div className='flex items-center gap-1.5'>
            <span className='text-sm font-semibold'>
              {selectedPage?.dpi ?? 0} DPI
            </span>
            <div
              className={cn(
                'h-2 w-2 rounded-full',
                selectedPage && selectedPage.dpi >= 300
                  ? 'bg-blue-500'
                  : 'bg-red-500'
              )}
            />
          </div>
        </div>

        {/* Confidence Score */}
        {pages.length > 0 &&
          (() => {
            const { confident, total, percent } = deriveBatchConfidence(pages);
            const confidentEnough = percent >= CONFIDENCE_THRESHOLD;
            return (
              <>
                <p className='text-muted-foreground mb-2 text-xs font-semibold tracking-widest uppercase'>
                  {t('confidenceScore')}
                </p>
                <div className='bg-muted mb-2 h-2.5 w-full rounded-full'>
                  <div
                    className={cn(
                      'h-full rounded-full',
                      confidentEnough ? 'bg-blue-500' : 'bg-red-500'
                    )}
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <div className='flex justify-between text-xs'>
                  <span className='font-bold'>{percent}%</span>
                  <span className='text-muted-foreground'>
                    {t('confidenceThreshold', {
                      threshold: CONFIDENCE_THRESHOLD
                    })}
                  </span>
                </div>
                <p className='text-muted-foreground mt-1 text-xs'>
                  {t('confidenceBasis', { confident, total })}
                </p>
              </>
            );
          })()}
      </div>

      {/* Spacer + End Session + Reject Batch at bottom */}
      {!readOnly && (
        <div className='mt-auto space-y-2'>
          <Button
            variant='outline'
            className='w-full'
            disabled={!canEndSession || isEndingSession}
            onClick={onEndSession}
          >
            {isEndingSession ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : null}
            {t('endSession')}
          </Button>
          {onRejectBatch && (
            <Button
              variant='outline'
              className='border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive w-full'
              disabled={!canRejectBatch || isEndingSession}
              onClick={onRejectBatch}
            >
              <XCircle className='me-2 h-4 w-4' />
              {t('rejectBatch')}
            </Button>
          )}
        </div>
      )}
    </aside>
  );
}
